import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";

import { InventarioIAService } from "../inventario-ia.service";
import {
  CatCicloVida,
  InventarioIA,
} from "src/app/shared/model/iso42001/inventario-ia.model";

@Component({
  selector: "app-consulta-inventario-ia",
  standalone: true,
  templateUrl: "./consulta-inventario-ia.component.html",
  styleUrls: ["./consulta-inventario-ia.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class ConsultaInventarioIAComponent implements OnInit {
  inventarios: InventarioIA[] = [];
  ciclosVida: CatCicloVida[] = [];
  loading = false;

  filtro: InventarioIA = { nombreIa: "", estatus: "", idCicloVida: null };

  // Paginación (client-side, 10 por página); se ocultan los inactivos
  tamPagina = 10;
  paginaActual = 1;

  constructor(
    private service: InventarioIAService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.cargarCatalogos();
    this.consultar();
  }

  cargarCatalogos(): void {
    this.service.ciclosVida().subscribe({
      next: (res) => { this.ciclosVida = res.ciclosVida || []; this.cdr.detectChanges(); },
      error: () => { this.ciclosVida = []; this.cdr.detectChanges(); },
    });
  }

  consultar(): void {
    this.loading = true;
    this.service.consultar(this.filtro).subscribe({
      next: (res) => { this.inventarios = res.inventarios || []; this.paginaActual = 1; this.loading = false; this.cdr.detectChanges(); },
      error: () => { this.loading = false; this.cdr.detectChanges(); },
    });
  }

  limpiar(): void {
    this.filtro = { nombreIa: "", estatus: "", idCicloVida: null };
    this.consultar();
  }

  verDetalle(item: InventarioIA): void {
    this.router.navigate(["/admin/inventario-ia/detalle", item.idInventarioIa]);
  }

  obtenerPropietario(item: InventarioIA): string {
    return item.areaNombre || item.propietarioExterno || "—";
  }

  // ── Inactivos ocultos + paginación ──────────────────────────────────────────
  inventariosVisibles(): InventarioIA[] {
    return (this.inventarios || []).filter((i) => i.estatus !== "INACTIVO");
  }

  inventariosPagina(): InventarioIA[] {
    const inicio = (this.paginaNorm() - 1) * this.tamPagina;
    return this.inventariosVisibles().slice(inicio, inicio + this.tamPagina);
  }

  totalPaginas(): number {
    const n = this.inventariosVisibles().length;
    return n > 0 ? Math.ceil(n / this.tamPagina) : 1;
  }

  paginaNorm(): number {
    return Math.min(Math.max(this.paginaActual, 1), this.totalPaginas());
  }

  rangoInicio(): number {
    return this.inventariosVisibles().length === 0 ? 0 : (this.paginaNorm() - 1) * this.tamPagina + 1;
  }

  rangoFin(): number {
    return Math.min(this.paginaNorm() * this.tamPagina, this.inventariosVisibles().length);
  }

  irPagina(p: number): void {
    const total = this.totalPaginas();
    if (p < 1 || p > total || p === this.paginaNorm()) { return; }
    this.paginaActual = p;
    this.cdr.detectChanges();
  }

  primeraPagina(): void { this.irPagina(1); }
  paginaAnterior(): void { this.irPagina(this.paginaNorm() - 1); }
  paginaSiguiente(): void { this.irPagina(this.paginaNorm() + 1); }
  ultimaPagina(): void { this.irPagina(this.totalPaginas()); }

  getPaginationItems(): Array<number | string> {
    const total = this.totalPaginas();
    if (total <= 1) { return [1]; }
    const a = this.paginaNorm();
    const set = new Set<number>([1, total, a - 1, a, a + 1]);
    const ord = Array.from(set).filter((p) => p >= 1 && p <= total).sort((x, y) => x - y);
    const items: Array<number | string> = [];
    for (let k = 0; k < ord.length; k++) {
      const p = ord[k];
      const prev = ord[k - 1];
      if (prev && p - prev > 1) { items.push("e" + prev); }
      items.push(p);
    }
    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }
}
